import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { jobAlertConfig } from '../data/jobAlertConfig';

export default function Header({ page }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  const locale = sessionStorage.getItem('versant_locale') || 'en';
  
  const translations = {
    en: {
      home: 'Home',
      ebook: 'Ebook 📘',
      blog: 'Blog',
      about: 'About Us',
      contact: 'Contact Us',
      login: 'Log In',
      logout: 'Log Out',
      upgrade: 'Upgrade to Pro',
      dashboard: 'Dashboard',
    },
    es: {
      home: 'Inicio',
      ebook: 'eBook 📘',
      blog: 'Blog',
      about: 'Nosotros',
      contact: 'Contacto',
      login: 'Iniciar Sesión',
      logout: 'Cerrar Sesión',
      upgrade: 'Mejorar a Pro',
      dashboard: 'Panel',
    },
    pt: {
      home: 'Início',
      ebook: 'eBook 📘',
      blog: 'Blog',
      about: 'Sobre Nós',
      contact: 'Contato',
      login: 'Entrar',
      logout: 'Sair',
      upgrade: 'Melhorar para Pro',
      dashboard: 'Painel',
    },
    ja: {
      home: 'ホーム',
      ebook: '電子書籍 📘',
      blog: 'ブログ',
      about: '会社概要',
      contact: 'お問い合わせ',
      login: 'ログイン',
      logout: 'ログアウト',
      upgrade: 'Proにアップグレード',
      dashboard: 'ダッシュボード',
    }
  };

  const t = translations[locale] || translations.en;

  const getHomeLink = () => {
    if (locale === 'es') return '/es';
    if (locale === 'pt') return '/pt';
    if (locale === 'ja') return '/ja';
    return '/';
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await trackFunnelEvent('user_logout');
    await signOut(auth);
    navigate(getHomeLink());
  };

  // Links shown when user IS logged in
  const LoggedInLinks = () => (
    <>
      <Link to="/dashboard" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.dashboard}</Link>
      <Link to="/ebook" onClick={() => setIsOpen(false)} className="font-black bg-gradient-to-r from-[#d4af37] via-[#f9e8a2] to-[#aa7c11] bg-clip-text text-transparent drop-shadow-[0_2px_6px_rgba(212,175,55,0.4)] hover:brightness-125 transition-all duration-200">{t.ebook}</Link>
      <Link to="/blog" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.blog}</Link>
      <Link to="/about" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.about}</Link>
      <Link to="/contact" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.contact}</Link>
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-3 mt-4 md:mt-0 md:ml-4 border-t md:border-t-0 border-green-600 pt-4 md:pt-0">
        <Link to="/pricing" onClick={() => setIsOpen(false)} className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-5 py-2.5 rounded-full uppercase tracking-wider text-xs transition-transform hover:-translate-y-0.5 shadow-lg shadow-emerald-500/30 flex items-center gap-2 justify-center w-max">
          <span>⭐</span> {t.upgrade}
        </Link>
        <button onClick={handleLogout} className="font-bold md:text-white text-green-800 hover:text-green-200 transition-colors text-sm">
          {t.logout}
        </button>
      </div>
    </>
  );

  // Links shown when user is NOT logged in
  const GuestLinks = () => (
    <>
      <Link to={getHomeLink()} onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.home}</Link>
      <Link to="/ebook" onClick={() => setIsOpen(false)} className="font-black bg-gradient-to-r from-[#d4af37] via-[#f9e8a2] to-[#aa7c11] bg-clip-text text-transparent drop-shadow-[0_2px_6px_rgba(212,175,55,0.4)] hover:brightness-125 transition-all duration-200">{t.ebook}</Link>
      <Link to="/blog" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.blog}</Link>
      <Link to="/about" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.about}</Link>
      <Link to="/contact" onClick={() => setIsOpen(false)} className="hover:text-green-200 font-medium md:text-white text-green-800">{t.contact}</Link>
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 mt-4 md:mt-0 md:ml-4 border-t md:border-t-0 border-green-600 pt-4 md:pt-0">
        <Link to="/login" onClick={() => setIsOpen(false)} className="font-bold md:text-white text-green-800 hover:text-green-200 transition-colors">
          {t.login}
        </Link>
        <Link to="/pricing" onClick={() => setIsOpen(false)} className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-5 py-2.5 rounded-full uppercase tracking-wider text-xs transition-transform hover:-translate-y-0.5 shadow-lg shadow-emerald-500/30 flex items-center gap-2 justify-center w-max">
          <span>⭐</span> {t.upgrade}
        </Link>
      </div>
    </>
  );

  const targetDateStr = (() => {
    const baseDate = new Date('May 25, 2026 23:59:59').getTime();
    const cycle = 10 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let target = baseDate;
    if (now > baseDate) {
      target = baseDate + (Math.floor((now - baseDate) / cycle) + 1) * cycle;
    }
    return new Date(target).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  })();

  return (
    <>
      <div className="sticky top-0 z-[60] shadow-md w-full">
        {/* On Desktop: Single horizontal bar containing both side-by-side */}
        <div className="hidden md:flex bg-red-600 text-white text-xs lg:text-sm font-bold tracking-wide py-2.5 px-6 justify-between items-center w-full">
          {/* Sale Alert (Left Side) */}
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => navigate('/pricing')}>
            <span className="animate-pulse text-sm">🔴</span>
            <span>LIMITED-TIME OFFER: Get Lifetime Access of Tests. Offer ends {targetDateStr}!</span>
            <span className="underline decoration-white/50 ml-1">Claim Offer ➔</span>
          </div>

          {/* Job Alert (Right Side) */}
          {jobAlertConfig.showBar && (
            <div className="flex items-center gap-3">
              {/* Vertical Divider */}
              <div className="h-4 w-[1px] bg-white/20"></div>
              
              <a
                href={jobAlertConfig.channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackFunnelEvent('whatsapp_clicked', { type: 'header_job_bar' })}
                className="flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span>{jobAlertConfig.alertText}</span>
                <span className="bg-indigo-950/40 border border-indigo-500/20 px-2 py-0.5 rounded text-[10px] uppercase font-black text-indigo-200 whitespace-nowrap flex items-center gap-1">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-[#25D366]">
                    <path d="M12.031 21.428h-.011a9.882 9.882 0 0 1-5.04-1.38l-.36-.214-3.75 1.05 1.054-3.66-.235-.373A9.881 9.881 0 0 1 2.1 11.831C2.1 6.353 6.556 1.88 12.042 1.88c2.664 0 5.166 1.042 7.042 2.92a9.921 9.921 0 0 1 2.91 7.03c0 5.478-4.455 9.95-9.963 9.998zm0-17.914c-4.382 0-7.95 3.568-7.95 7.95 0 1.4.363 2.768 1.055 3.972l1.24 2.164-2.124 7.375 7.545-2.107 2.13 1.22c1.192.68 2.535 1.037 3.923 1.037h.007c4.38 0 7.947-3.568 7.947-7.95 0-2.123-.827-4.12-2.327-5.62A7.944 7.944 0 0 0 12.03 3.514zm4.27 10.978c-.234-.117-1.385-.683-1.6-.762-.214-.08-.37-.117-.527.117-.156.234-.606.762-.743.918-.136.156-.273.176-.507.059-.234-.117-.99-.364-1.885-1.16-.697-.62-1.168-1.386-1.305-1.62-.136-.234-.014-.36.103-.477.106-.106.234-.273.351-.41.117-.137.156-.234.234-.39.078-.156.039-.293-.02-.41-.059-.117-.527-1.27-.723-1.74-.191-.46-.386-.398-.527-.406-.136-.008-.293-.008-.45-.008a.866.866 0 0 0-.625.293c-.214.234-.82.802-.82 1.954 0 1.153.84 2.266.957 2.422.117.156 1.652 2.524 4 3.48.558.226.992.36 1.332.46.56.178 1.07.153 1.473.093.453-.067 1.385-.566 1.58-1.114.195-.547.195-1.016.136-1.114-.059-.098-.215-.156-.45-.273z"/>
                  </svg>
                  <span>{jobAlertConfig.ctaText}</span>
                </span>
              </a>
            </div>
          )}
        </div>

        {/* On Mobile: Stacked view (Sale on top, Job Alert below it) */}
        <div className="flex md:hidden flex-col w-full text-white text-xs font-bold tracking-wide">
          {/* Sale Banner */}
          <div className="bg-red-600 py-2 px-4 text-center flex items-center justify-center gap-1.5 cursor-pointer" onClick={() => navigate('/pricing')}>
            <span className="animate-pulse">🔴</span>
            <span>LIMITED-TIME OFFER: Ends {targetDateStr}!</span>
            <span className="underline decoration-white/50 ml-1">Claim ➔</span>
          </div>
          {/* Job Alert Banner */}
          {jobAlertConfig.showBar && (
            <a
              href={jobAlertConfig.channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFunnelEvent('whatsapp_clicked', { type: 'header_job_bar' })}
              className="bg-indigo-800 py-2 px-4 text-center flex items-center justify-center gap-1.5 border-t border-indigo-900/20"
            >
              <span className="flex h-1.5 w-1.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
              </span>
              <span className="text-[11px] truncate">{jobAlertConfig.alertText}</span>
              <span className="bg-indigo-950/40 px-1.5 py-0.5 rounded text-[9px] font-black uppercase text-indigo-200 whitespace-nowrap flex items-center gap-0.5">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-[#25D366]">
                  <path d="M12.031 21.428h-.011a9.882 9.882 0 0 1-5.04-1.38l-.36-.214-3.75 1.05 1.054-3.66-.235-.373A9.881 9.881 0 0 1 2.1 11.831C2.1 6.353 6.556 1.88 12.042 1.88c2.664 0 5.166 1.042 7.042 2.92a9.921 9.921 0 0 1 2.91 7.03c0 5.478-4.455 9.95-9.963 9.998zm0-17.914c-4.382 0-7.95 3.568-7.95 7.95 0 1.4.363 2.768 1.055 3.972l1.24 2.164-2.124 7.375 7.545-2.107 2.13 1.22c1.192.68 2.535 1.037 3.923 1.037h.007c4.38 0 7.947-3.568 7.947-7.95 0-2.123-.827-4.12-2.327-5.62A7.944 7.944 0 0 0 12.03 3.514zm4.27 10.978c-.234-.117-1.385-.683-1.6-.762-.214-.08-.37-.117-.527.117-.156.234-.606.762-.743.918-.136.156-.273.176-.507.059-.234-.117-.99-.364-1.885-1.16-.697-.62-1.168-1.386-1.305-1.62-.136-.234-.014-.36.103-.477.106-.106.234-.273.351-.41.117-.137.156-.234.234-.39.078-.156.039-.293-.02-.41-.059-.117-.527-1.27-.723-1.74-.191-.46-.386-.398-.527-.406-.136-.008-.293-.008-.45-.008a.866.866 0 0 0-.625.293c-.214.234-.82.802-.82 1.954 0 1.153.84 2.266.957 2.422.117.156 1.652 2.524 4 3.48.558.226.992.36 1.332.46.56.178 1.07.153 1.473.093.453-.067 1.385-.566 1.58-1.114.195-.547.195-1.016.136-1.114-.059-.098-.215-.156-.45-.273z"/>
                </svg>
                <span>Join</span>
              </span>
            </a>
          )}
        </div>
      </div>

      <nav className="bg-green-700 text-white px-4 py-3 shadow-md md:px-6 md:py-4 relative z-50">
        <div className="container mx-auto flex justify-between items-center">
          {/* Logo & Mobile WhatsApp Button container */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to={currentUser ? '/dashboard' : getHomeLink()} className="flex items-center space-x-2 mr-4 xs:mr-5">
              <div className="w-8 h-8 bg-green-500 rounded-full"></div>
              <span className="text-xl font-bold">Versant Test</span>
            </Link>
            
            {/* Mobile-only WhatsApp Channel Button */}
            <a
              href="https://whatsapp.com/channel/0029VbDcPbdJf05b1oC45s1i"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackFunnelEvent('whatsapp_clicked', { type: 'header_mobile' })}
              className="md:hidden flex items-center gap-1 bg-[#25D366] hover:bg-[#20ba59] text-white font-extrabold px-3 py-1.5 rounded-full uppercase tracking-wider text-[9px] xs:text-[10px] border border-white/10 whitespace-nowrap shadow-sm"
            >
              <span>🔥 Job Alerts</span>
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-6 lg:space-x-8 items-center">
            {currentUser ? <LoggedInLinks /> : <GuestLinks />}
          </div>

          {/* Mobile Hamburger Button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-white hover:text-green-200 focus:outline-none">
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-xl border-t border-green-600 py-4 px-4 flex flex-col space-y-4 z-50">
            {currentUser ? <LoggedInLinks /> : <GuestLinks />}
          </div>
        )}
      </nav>
    </>
  );
}
